/* =========================================================
   Anew Sleep — PDP main-product section JS
   - Custom dropdown bridge to hidden native <select>
   - Quantity stepper
   - Delivery line: countdown to the shop's cutoff + expected delivery dates
   - Add-on swatch + checkbox UI
   - Upsell card variant select → price column sync
   - Per-variant galleries: swap the whole gallery on variant change
   Gallery state, zoom, and slider are handled by Dawn's
   media-gallery.js + product-modal.js + slider-component.
   All event handlers are delegated on the document so Dawn's section
   re-render (on variant change) does not require re-binding.
   ========================================================= */

(function () {
  'use strict';

  // ---------- Variant dropdowns ----------
  function closeAllDropdowns(except) {
    document.querySelectorAll('[data-anew-sel][data-open="true"]').forEach((sel) => {
      if (sel !== except) {
        sel.removeAttribute('data-open');
        const trigger = sel.querySelector('[data-anew-sel-trigger]');
        const menu = sel.querySelector('.anew-sel__menu');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (menu) menu.setAttribute('hidden', '');
      }
    });
  }

  function openDropdown(sel) {
    sel.setAttribute('data-open', 'true');
    const trigger = sel.querySelector('[data-anew-sel-trigger]');
    const menu = sel.querySelector('.anew-sel__menu');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (menu) menu.removeAttribute('hidden');
  }

  function selectOption(sel, optionEl) {
    if (optionEl.classList.contains('is-unavailable')) return;
    const value = optionEl.dataset.value;
    const native = sel.querySelector('.anew-sel__native');
    if (!native || native.value === value) {
      closeAllDropdowns();
      return;
    }
    native.value = value;
    // Update the visible label + meta optimistically — Dawn's re-render will overwrite anyway
    const valueLabel = sel.querySelector('[data-anew-sel-value]');
    if (valueLabel) valueLabel.textContent = optionEl.querySelector('.anew-sel__option-label')?.textContent.trim() || value;
    const triggerMeta = sel.querySelector('[data-anew-sel-trigger-meta]');
    if (triggerMeta) {
      const metaLabel = (optionEl.dataset.meta || '').trim();
      triggerMeta.textContent = metaLabel;
      triggerMeta.toggleAttribute('hidden', metaLabel === '');
    }
    sel.querySelectorAll('[data-anew-sel-option]').forEach((opt) => {
      opt.setAttribute('aria-selected', opt === optionEl ? 'true' : 'false');
    });
    closeAllDropdowns();
    // Bubble up to Dawn's <variant-selects> web component
    native.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-anew-sel-trigger]');
    if (trigger) {
      const sel = trigger.closest('[data-anew-sel]');
      const isOpen = sel.getAttribute('data-open') === 'true';
      closeAllDropdowns(isOpen ? null : sel);
      if (!isOpen) openDropdown(sel);
      return;
    }
    const option = e.target.closest('[data-anew-sel-option]');
    if (option) {
      const sel = option.closest('[data-anew-sel]');
      selectOption(sel, option);
      return;
    }
    // Click outside — close any open dropdown
    if (!e.target.closest('[data-anew-sel]')) {
      closeAllDropdowns();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllDropdowns();
  });

  // ---------- Variant pills ----------
  // The pill shape of the picker (see snippets/pdp-variant-dropdowns.liquid)
  // is a native radio group, so there is no state to manage here — only the
  // same bridge the dropdown uses: write the value onto the hidden <select>
  // and let Dawn's own change handling do the rest.
  document.addEventListener('change', (e) => {
    const pill = e.target.closest('[data-anew-pill]');
    if (!pill) return;

    const sel = pill.closest('[data-anew-sel]');
    const native = sel && sel.querySelector('.anew-sel__native');
    if (!native || native.value === pill.value) return;

    native.value = pill.value;
    native.dispatchEvent(new Event('change', { bubbles: true }));

    // A swatch row carries circles and no words, so the chosen colour is named
    // beside the option's own label. Nothing here runs for a word pill, which
    // says its own value.
    const chosen = sel.querySelector('[data-anew-sel-chosen]');
    if (chosen) chosen.textContent = pill.dataset.label || pill.value;
  });

  // ---------- Quantity stepper ----------
  document.addEventListener('click', (e) => {
    const dec = e.target.closest('[data-anew-qty-decrement]');
    const inc = e.target.closest('[data-anew-qty-increment]');
    if (!dec && !inc) return;
    const wrap = (dec || inc).closest('.anew-qty');
    const input = wrap?.querySelector('[data-anew-qty-input]');
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    const step = parseInt(input.step || '1', 10) || 1;
    const current = parseInt(input.value || String(min), 10) || min;
    const next = dec ? Math.max(min, current - step) : current + step;
    if (next !== current) {
      input.value = String(next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // ---------- Delivery line ----------
  // Two states off one cutoff: on a weekday before it the parcel ships today
  // (with a countdown), otherwise on the next weekday; either way it arrives
  // on the 1st–2nd delivery day after shipping. Parcels ship Mon–Fri but are
  // delivered Mon–Sat — see snippets/pdp-delivery-line.liquid, which runs the
  // same walk server-side. Both sentences are already
  // in the DOM — the section renders them and hides one — so this toggles
  // `hidden`, ticks the clock and rewrites the two date ranges. The weekday
  // names and templates come from the section, so the copy stays in locales.
  //
  // The clock is the SHOP's, not the browser's. `setHours()` on a local Date
  // reads a 14:00 DK cutoff as 14:00 wherever the shopper happens to be, so
  // the section stamps its own wall clock, date and the epoch it rendered at,
  // and this advances them by the real seconds elapsed since. All date maths
  // runs in UTC on that shop-local date, so the browser's zone never enters.
  function pad2(n) { return String(n).padStart(2, '0'); }

  function parseCutoff(raw) {
    // Accepts "HH:MM" (e.g. "14:00"). Falls back to 14:00 on malformed input.
    const match = /^(\d{1,2}):(\d{2})$/.exec((raw || '').trim());
    if (!match) return 14 * 3600;
    const h = Math.min(23, Math.max(0, parseInt(match[1], 10)));
    const m = Math.min(59, Math.max(0, parseInt(match[2], 10)));
    return h * 3600 + m * 60;
  }

  const DAY = 86_400;

  // The shop's local moment, right now: `seconds` since its midnight and
  // `date`, a UTC Date standing for its calendar day. Null when the stamps
  // are missing or unparseable — the caller then leaves the server's render
  // alone rather than compute from the wrong clock.
  function shopNow(wrap) {
    const clock = /^(\d{2}):(\d{2}):(\d{2})$/.exec(wrap.dataset.anewDeliveryShopNow || '');
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wrap.dataset.anewDeliveryShopDate || '');
    const renderedAt = Number(wrap.dataset.anewDeliveryRenderedAt);
    if (!clock || !day || !Number.isFinite(renderedAt) || renderedAt <= 0) return null;
    const atRender =
      parseInt(clock[1], 10) * 3600 + parseInt(clock[2], 10) * 60 + parseInt(clock[3], 10);
    const elapsed = Math.max(0, Math.floor(Date.now() / 1000) - renderedAt);
    const total = atRender + elapsed;
    const date = new Date(Date.UTC(
      parseInt(day[1], 10), parseInt(day[2], 10) - 1, parseInt(day[3], 10) + Math.floor(total / DAY)
    ));
    return { seconds: total % DAY, date };
  }

  function isWeekday(date) {
    const d = date.getUTCDay();
    return d !== 0 && d !== 6;
  }

  // Delivery-date labels for both states, from the shop's `date`: `before`
  // ships today, `after` ships on the first weekday after today. Each gets the
  // first two Mon–Sat days after its ship day.
  function deliveryLabels(wrap, date) {
    const names = (wrap.dataset.anewDeliveryWeekdays || '').split(',');
    const format = wrap.dataset.anewDeliveryDateFormat || '[w] [d]/[m]';
    const before = [];
    const after = [];
    let shipped = false;
    const cursor = new Date(date.getTime());
    for (let i = 0; i < 10 && (before.length < 2 || after.length < 2); i++) {
      cursor.setUTCDate(cursor.getUTCDate() + 1);
      if (cursor.getUTCDay() !== 0) {
        const label = format
          .replace('[w]', names[cursor.getUTCDay()] || '')
          .replace('[d]', String(cursor.getUTCDate()))
          .replace('[m]', String(cursor.getUTCMonth() + 1));
        if (before.length < 2) before.push(label);
        if (shipped && after.length < 2) after.push(label);
      }
      if (!shipped && isWeekday(cursor)) shipped = true;
    }
    return { before, after };
  }

  function range(wrap, from, to) {
    return (wrap.dataset.anewDeliveryRange || '[from]–[to]')
      .replace('[from]', from)
      .replace('[to]', to);
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function tickDelivery() {
    const wrappers = document.querySelectorAll('[data-anew-delivery]');
    if (!wrappers.length) return;

    wrappers.forEach((wrap) => {
      const countEl = wrap.querySelector('[data-anew-delivery-count]');
      const beforeEl = wrap.querySelector('[data-anew-delivery-before]');
      const afterEl = wrap.querySelector('[data-anew-delivery-after]');
      const now = shopNow(wrap);
      if (now === null) return;

      const cutoff = parseCutoff(wrap.dataset.anewDeliveryCutoff);
      const before = isWeekday(now.date) && now.seconds < cutoff;

      if (beforeEl) beforeEl.hidden = !before;
      if (afterEl) afterEl.hidden = before;

      const labels = deliveryLabels(wrap, now.date);
      setText(wrap.querySelector('[data-anew-delivery-dates="before"]'), range(wrap, labels.before[0], labels.before[1]));
      setText(wrap.querySelector('[data-anew-delivery-dates="after"]'), range(wrap, labels.after[0], labels.after[1]));

      if (!before || !countEl) return;

      const diff = cutoff - now.seconds;
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const sec = diff % 60;
      setText(countEl, h > 0 ? `${pad2(h)}:${pad2(m)}:${pad2(sec)}` : `${pad2(m)}:${pad2(sec)}`);
    });
  }

  setInterval(tickDelivery, 1000);
  tickDelivery();

  // ---------- Per-variant galleries ----------
  // A product tagged `galleri:per-variant` renders only the selected
  // variant's images (snippets/product-media-variant-hidden.liquid). Dawn's
  // product-info.js re-syncs the main slide list on a variant change but
  // never the thumbnail strip, so the strip would keep the first variant's
  // pictures. So the whole <media-gallery> is swapped for the fetched one: a
  // new element runs Dawn's constructor again and binds its own thumbnail and
  // slider listeners.
  //
  // One catch: by the time this event fires, Dawn's updateMedia has already
  // MOVED the new slides out of the fetched document into the live list, so
  // the fetched main list is empty. The new gallery therefore takes the live
  // main list (already right) and the fetched everything-else.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || !data.html || !data.sectionId) return;
      const current = document.getElementById(`MediaGallery-${data.sectionId}`);
      const next = data.html.getElementById(`MediaGallery-${data.sectionId}`);
      if (!current || !next) return;
      if (!current.hasAttribute('data-anew-variant-media') && !next.hasAttribute('data-anew-variant-media')) return;
      const replacement = document.importNode(next, true);
      const liveMain = current.querySelector('[id^="Slider-Gallery-"]');
      const newMain = replacement.querySelector('[id^="Slider-Gallery-"]');
      if (liveMain && newMain) newMain.replaceWith(liveMain);
      current.replaceWith(replacement);
    });
  }

  // ---------- Upsell cards ----------
  // The variant price sync lives in assets/upsell-card.js: the cart drawer
  // renders the same card on every page and never loads this file.
  //
  // The offer selector is the same story — assets/pdp-offers.js owns it,
  // because it also carries the <product-form> subclass the `items[]` form
  // needs and that belongs next to the element it patches.
})();
